import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

type ToolTheme = 'default' | 'barometre' | 'projector-watch' | 'align';

interface ThemeContextType {
  currentTool: ToolTheme;
  toolColors: {
    primary: string;
    secondary: string;
    accent: string;
    gradient: string;
    background: string;
  };
}

const defaultColors = {
  primary: '#54075B',
  secondary: '#9333EA',
  accent: '#E879F9',
  gradient: 'from-purple-50 via-pink-50 to-purple-100',
  background: 'bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100'
};

const ThemeContext = createContext<ThemeContextType>({
  currentTool: 'default',
  toolColors: defaultColors
});

export const useToolTheme = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [currentTool, setCurrentTool] = useState<ToolTheme>('default');
  const [toolColors, setToolColors] = useState(defaultColors);

  useEffect(() => {
    const loadColors = async () => {
      try {
        const { data: colorSettings, error } = await supabase
          .from('admin_settings')
          .select('value')
          .eq('key', 'colors')
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading colors:', error);
          return;
        }

        if (colorSettings?.value) {
          const colors = colorSettings.value;
          const primary = colors.find((c: any) => c.name === 'primary')?.value || defaultColors.primary;
          const secondary = colors.find((c: any) => c.name === 'secondary')?.value || defaultColors.secondary;
          const accent = colors.find((c: any) => c.name === 'accent')?.value || defaultColors.accent;

          setToolColors({
            primary,
            secondary,
            accent,
            gradient: `from-${primary} via-${secondary} to-${accent}`,
            background: `bg-gradient-to-br from-${primary} via-${secondary} to-${accent}`
          });
        }
      } catch (error) {
        console.error('Error loading color settings:', error);
      }
    };

    loadColors();

    // Subscribe to color changes
    const subscription = supabase
      .channel('color_settings_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'admin_settings', filter: 'key=eq.colors' },
        () => {
          loadColors();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    // Determine current tool based on path
    if (location.pathname.startsWith('/barometre')) {
      setCurrentTool('barometre');
    } else if (location.pathname.startsWith('/projector-watch')) {
      setCurrentTool('projector-watch');
    } else if (location.pathname.startsWith('/align')) {
      setCurrentTool('align');
    } else {
      setCurrentTool('default');
    }

    // Update CSS variables
    document.documentElement.style.setProperty('--tool-primary', toolColors.primary);
    document.documentElement.style.setProperty('--tool-secondary', toolColors.secondary);
    document.documentElement.style.setProperty('--tool-accent', toolColors.accent);
  }, [location.pathname, toolColors]);

  return (
    <ThemeContext.Provider value={{ currentTool, toolColors }}>
      {children}
    </ThemeContext.Provider>
  );
};